
import { computed, defineComponent, PropType, Ref, ref } from 'vue';
import { keys, map, sortBy, zipObject } from 'lodash';
import breakpointService from '../breakpoints/breakpoints.service';
import loggingService from '../../logging.service';
import breakpointsState from '../breakpoints/breakpoints-state.observable';
import responsiveImageService from './responsive-image.service';
import generateSrcset from '@/core/responsive/image/helpers/generate-srcset';
import { ImageFit } from '@/core/responsive/image/image.types';
import PressModeDownloadButton from '@/shared/media/press-mode/PressModeDownloadButton.vue';
import generateSrc from '@/core/responsive/image/helpers/generate-src';

export interface IResponsiveImageWidthOnScreenConfig {
    [key: string]: number;
}

export default defineComponent({
    name: 'ResponsiveImage',
    components: { PressModeDownloadButton },
    props: {
        imageUrl: {
            type: String,
            default: '',
        },
        widthOnScreen: {
            type: [Object, Number],
            default: 100,
        },
        lazy: {
            type: Boolean,
            default: true,
            required: false,
        },
        isProductImage: {
            type: Boolean,
            default: false,
            required: false,
        },
        scrim: {
            type: Boolean,
            default: false,
            required: false,
        },
        offset: {
            type: Number,
            default: 200,
            required: false,
        },
        bgColor: {
            type: String,
            default: 'transparent',
            required: false,
        },
        alt: {
            type: String,
            default: '',
            required: false,
        },
        focus: {
            type: Boolean,
            default: false,
        },
        zoom: {
            type: Boolean,
            default: false,
        },
        focusPoints: {
            type: String,
            default: '',
        },
        cacheBust: {
            type: [String, Number],
            required: false,
            default: '1',
        },
        flip: {
            type: [Boolean],
            default: false,
        },
        mode: {
            type: String as PropType<ImageFit>,
            default: 'cover',
            validator: (value: string) => { return ['crop', 'contain', 'cover'].indexOf(value) !== -1; },
        },
        /* Can take a single aspect ratio number, or an object with a number pr. breakpoint. If a 'default' is provided is will be used when no breakpoint is active:
            {
                 xs: 16/9,
                 min-sm,max-md: 4/3
                 default: 1/2
            }
         */
        aspectRatio: {
            type: [Number, Object],
            default: 16 / 9,
        },
        originalHeight: {
            type: [Number, null],
            default: null,
        },
        originalWidth: {
            type: [Number, null],
            default: null,
        },
        hidePressModeButton: {
            type: [Boolean],
            default: false,
            required: false,
        },
    },
    setup(props) {
        const sizes: Array<number> = responsiveImageService.getSizes();
        const loadEventFired: Ref<boolean> = ref(false);
        const image: Ref<HTMLElement | null> = ref(null);

        const activeAspectRatio = computed(() => {
            if (typeof props.aspectRatio === 'number') {
                return props.aspectRatio;
            }

            // Else - object with aspect-ratio pr. breakpoint-def.
            const activeBpDef = Object.keys(props.aspectRatio).find((bpDef) => breakpointsState.isBreakpointActive(bpDef));
            const activeOrDefault = activeBpDef ? props.aspectRatio[activeBpDef] : (props.aspectRatio as any).default;

            if (!activeOrDefault) {
                throw new Error('Provide a breakpoint-definition for all cases or a default');
            }

            return activeOrDefault;
        });

        const maybeLazyInit = computed(() => {
            return props.lazy ? 'lazy-init' : 'div';
        });

        const style = computed(() => {
            const padding = `${(100 / activeAspectRatio.value).toFixed(2)}%`;
            return { paddingTop: padding, background: `${props.bgColor}` };
        });

        const sortConfigBySize = (config: IResponsiveImageWidthOnScreenConfig) => {
            const sortedKeys = sortBy(keys(config), (key) => {
                return config[key];
            });

            return zipObject(
                sortedKeys,
                map(sortedKeys, function(key) {
                    return config[key];
                }),
            );
        };

        const sizesFromWidthOnScreen = computed(() => {
            let sizes: string = '';

            if (typeof props.widthOnScreen === 'object') {
                sizes = map(sortConfigBySize(props.widthOnScreen), (width, screen) => {
                    if (!breakpointService.getBreakpoint(screen)) {
                        const errorMessage = `ResponsiveImage: Breakpoint ${screen} is not defined for imageUrl ${
                            props.imageUrl
                        }`;
                        loggingService.warn(errorMessage);
                    }
                    return `(min-width: ${breakpointService.getBreakpoint(screen).min}px) ${width}vw`;
                }).join(',');
                sizes += ', 100vw';
            } else {
                sizes = `${props.widthOnScreen}vw`;
            }

            return sizes;
        });

        const srcOptions = computed(() => {
            const zoom = props.zoom && props.focusPoints;
            const focus = props.focus && props.focusPoints;
            const focusPoints = props.focusPoints;
            return {
                aspectRatio: activeAspectRatio.value,
                background: props.bgColor,
                cacheBust: props.cacheBust,
                fit: props.mode,
                isProductImage: props.isProductImage,
                originalHeight: props.originalHeight,
                originalWidth: props.originalWidth,
                focus: focus,
                zoom: zoom,
                focusPoints: focusPoints,
            };
        });

        const srcset = computed(() => {
            return generateSrcset(props.imageUrl, srcOptions.value, sizes);
        });

        const src = computed(() => {
            const smallestSize = sizes[0];
            return generateSrc(props.imageUrl, { ...srcOptions.value, width: smallestSize });
        });

        const handleLoad = (): void => {
            loadEventFired.value = true;
        };

        return {
            loadEventFired,
            src,
            style,
            maybeLazyInit,
            sizesFromWidthOnScreen,
            image,
            srcset,
            handleLoad,
        };
    },
});
